// Google Analytics 4 wrapper. Loads gtag.js only when a measurement ID is
// configured (REACT_APP_GA_MEASUREMENT_ID) AND this is a production build of
// the production environment, so `npm start` and build:dev deploys send nothing.
//
// Privacy rules — this app holds patient data, none of it goes to Google:
//   - no names, mobile numbers, emails, call channels, or tokens in any event
//   - page paths are sent as their route PATTERN, never the real URL. Routes
//     here embed patient names and call ids (/inPerson/.../:firstName/...),
//     so a path that matches no known pattern is sent as "/(other)"
//   - the only user property is a coarse role for audience segmentation
import { matchPath } from "react-router-dom";

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Keep in sync with <Routes> in src/router/index.js.
const ROUTE_PATTERNS = [
  "/",
  "/dashboard",
  "/call/:channel/:mode/:id/:uid",
  "/set-availability/:available",
  "/calendar",
  "/profile",
  "/events",
  "/appointment-detail/:aid/:uid",
  "/upcoming-appointments/:upcoming",
  "/createEvent",
  "/report",
  "/appointment-history",
  "/session-summary",
  "/studentVideo/:show/:id/:channel",
  "/inPerson/:channel/:mode/:id/:uid/:title/:firstName/:lastName/:date/:startTime",
  "/chat/:channel/:mode/:id/:uid",
  "/logout",
  "/updateProfile",
  "/user-risk",
  "/help_center",
  "/support",
  "/member-summary-report/:aid/:uid",
];

let initialized = false;

function gtag() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}

export function initAnalytics() {
  if (
    initialized ||
    !GA_ID ||
    process.env.NODE_ENV !== "production" ||
    process.env.REACT_APP_ENV !== "production"
  )
    return;
  initialized = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  gtag("js", new Date());
  // send_page_view off: the router fires page_view explicitly on every
  // route change (the initial load included), so we don't double-count.
  gtag("config", GA_ID, { send_page_view: false });
}

// pathname is router-relative (basename already stripped).
export function normalizePath(pathname) {
  const pattern = ROUTE_PATTERNS.find((p) => matchPath(p, pathname));
  return pattern || "/(other)";
}

export function trackPageView(pathname) {
  if (!initialized) return;
  const page_path = normalizePath(pathname);
  gtag("event", "page_view", {
    page_path,
    page_title: page_path,
    // Deliberately NOT window.location.href — real URLs carry patient data.
    page_location: window.location.origin + page_path,
  });
}

export function trackEvent(name, params = {}) {
  if (!initialized) return;
  gtag("event", name, params);
}

export function setAnalyticsRole(role) {
  if (!initialized || !role) return;
  gtag("set", "user_properties", { app_role: String(role).toLowerCase() });
}
